import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import 'chartjs-adapter-date-fns';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  annotationPlugin
);

const TimeSeriesChart = ({
  title,
  series,
  yAxisLabel,
  timeRange = "24h",
  showLines = true,
  referenceTimestamp,
  timeZone = 'UTC',
  showLegend = true,
  showTitle = true,
  chartHeight,
}) => {
  // Handle empty or invalid series data
  const validSeries = Array.isArray(series) && series.length > 0 ? series : [];
  
  // Check if there's actually data to display
  const hasData = validSeries.length > 0 && validSeries.some(s => 
    Array.isArray(s.data) && s.data.length > 0
  );
  
  // If no data, show empty state
  if (!hasData) {
    return (
      <div className="chart-container-wrapper">
        {showTitle && <h4 className="chart-title">{title}</h4>}
        <div className="chart-canvas-container">
          <div className="blank-chart-placeholder">
            <p>No data available</p>
          </div>
        </div>
      </div>
    );
  }

  // Time range configurations for X-axis
  const getTimeRangeConfig = (range) => {
    const now = new Date();
    
    // Handle custom range objects
    if (typeof range === 'object' && range.type === 'custom') {
      const fromDate = new Date(range.from.endsWith('Z') ? range.from : range.from + 'Z');
      const toDate = new Date(range.to.endsWith('Z') ? range.to : range.to + 'Z');
      const diffMs = toDate.getTime() - fromDate.getTime();
      const diffDays = diffMs / (1000 * 60 * 60 * 24);
      
      // Configure based on custom range duration
      if (diffDays <= 1) {
        return {
          min: fromDate,
          max: toDate,
          unit: 'hour',
          stepSize: 2,
          displayFormat: 'HH:mm'
        };
      } else if (diffDays <= 7) {
        return {
          min: fromDate,
          max: toDate,
          unit: 'day',
          stepSize: 1,
          displayFormat: 'MMM dd HH:mm'
        };
      } else if (diffDays <= 90) {
        return {
          min: fromDate,
          max: toDate,
          unit: 'day',
          stepSize: Math.ceil(diffDays / 10),
          displayFormat: 'MMM dd'
        };
      } else {
        return {
          min: fromDate,
          max: toDate,
          unit: 'month',
          stepSize: 1,
          displayFormat: 'MMM yyyy'
        };
      }
    }
    
    // Handle preset string ranges
    const configs = {
      '5m': {
        min: new Date(now.getTime() - 5 * 60 * 1000),
        max: now,
        unit: 'minute',
        stepSize: 1,
        displayFormat: 'HH:mm:ss'
      },
      '15m': {
        min: new Date(now.getTime() - 15 * 60 * 1000),
        max: now,
        unit: 'minute',
        stepSize: 2,
        displayFormat: 'HH:mm'
      },
      '30m': {
        min: new Date(now.getTime() - 30 * 60 * 1000),
        max: now,
        unit: 'minute',
        stepSize: 5,
        displayFormat: 'HH:mm'
      },
      '1h': {
        min: new Date(now.getTime() - 1 * 60 * 60 * 1000),
        max: now,
        unit: 'minute',
        stepSize: 10,
        displayFormat: 'HH:mm'
      },
      '3h': {
        min: new Date(now.getTime() - 3 * 60 * 60 * 1000),
        max: now,
        unit: 'minute',
        stepSize: 30,
        displayFormat: 'HH:mm'
      },
      '6h': {
        min: new Date(now.getTime() - 6 * 60 * 60 * 1000),
        max: now,
        unit: 'hour',
        stepSize: 1,
        displayFormat: 'HH:mm'
      },
      '12h': {
        min: new Date(now.getTime() - 12 * 60 * 60 * 1000),
        max: now,
        unit: 'hour',
        stepSize: 2,
        displayFormat: 'HH:mm'
      },
      '24h': {
        min: new Date(now.getTime() - 24 * 60 * 60 * 1000),
        max: now,
        unit: 'hour',
        stepSize: 3,
        displayFormat: 'HH:mm'
      },
      '2d': {
        min: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000),
        max: now,
        unit: 'hour',
        stepSize: 6,
        displayFormat: 'MMM dd HH:mm'
      },
      '7d': {
        min: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000),
        max: now,
        unit: 'day',
        stepSize: 1,
        displayFormat: 'MMM dd'
      },
      '14d': {
        min: new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000),
        max: now,
        unit: 'day',
        stepSize: 1,
        displayFormat: 'MMM dd'
      },
      '30d': {
        min: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000),
        max: now,
        unit: 'day',
        stepSize: 3,
        displayFormat: 'MMM dd'
      },
      '90d': {
        min: new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000),
        max: now,
        unit: 'day',
        stepSize: 7,
        displayFormat: 'MMM dd'
      }
    };
    return configs[range] || configs['24h'];
  };

  const timeConfig = getTimeRangeConfig(timeRange);
  const chartData = {
    datasets: validSeries.map((s, index) => ({
      label: s.name,
      data: s.data,
      borderColor: s.color,
      backgroundColor: s.color + '20', // Add transparency
      borderWidth: showLines ? 2 : 0, // No border for scatter plots
      fill: false,
      tension: 0.1,
      pointHoverRadius: showLines ? 4 : 6,
      borderDash: s.borderDash,
      pointRadius: s.pointRadius ?? (showLines ? 0 : 4), // Show points for scatter plots
      pointStyle: s.pointStyle,
      pointBackgroundColor: s.color,
      pointBorderColor: s.color,
      showLine: showLines, // Control line visibility
      hidden: s.hidden || false, // Respect hidden property from data
      spanGaps: false,
    }))
  };

  const displayTimeZone = timeZone === 'local'
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : timeZone;

  const formatDateTime = (date, includeTimeZone = false) => {
    const options = includeTimeZone
      ? { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZoneName: 'short' }
      : { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false };
    if (displayTimeZone) options.timeZone = displayTimeZone;
    return new Intl.DateTimeFormat(undefined, options).format(date);
  };

  const formatTick = (value, format) => {
    const date = new Date(value);
    const options = format === 'HH:mm:ss'
      ? { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }
      : format === 'HH:mm'
        ? { hour: '2-digit', minute: '2-digit', hour12: false }
        : format === 'MMM dd HH:mm'
          ? { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }
          : format === 'MMM dd'
            ? { month: 'short', day: '2-digit' }
            : format === 'MMM yyyy'
              ? { month: 'short', year: 'numeric' }
              : {};
    if (displayTimeZone) options.timeZone = displayTimeZone;
    return new Intl.DateTimeFormat(undefined, options).format(date);
  };

  const refDate = referenceTimestamp ? new Date(referenceTimestamp.endsWith('Z') ? referenceTimestamp : referenceTimestamp + 'Z') : null;
  const refAnnotation = refDate && !Number.isNaN(refDate.getTime()) ? {
    annotation: {
      annotations: {
        referenceLine: {
          type: 'line',
          xMin: refDate,
          xMax: refDate,
          borderColor: '#e74c3c',
          borderWidth: 2,
          borderDash: [6, 3],
          label: {
            display: true,
            content: 'Reference',
            position: 'start',
            backgroundColor: '#e74c3c',
            color: '#fff',
            font: { size: 10 },
          },
        },
      },
    },
  } : {};

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      ...refAnnotation,
      legend: {
        display: showLegend,
        position: 'top',
        labels: {
          usePointStyle: true,
          pointStyle: 'line',
          pointStyleWidth: 20,
          padding: 15,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        callbacks: {
          title: function(context) {
            const date = new Date(context[0].parsed.x);
            return formatDateTime(date, true);
          },
          label: function(context) {
            if (context.parsed.y == null) return `${context.dataset.label}: No data`;
            return `${context.dataset.label}: ${context.parsed.y.toFixed(2)} ${yAxisLabel || ''}`;
          }
        }
      }
    },
    scales: {
      x: {
        type: 'time',
        min: timeConfig.min,
        max: timeConfig.max,
        bounds: 'data',
        time: {
          unit: timeConfig.unit,
          stepSize: timeConfig.stepSize,
          displayFormats: {
            minute: timeConfig.displayFormat,
            hour: timeConfig.displayFormat,
            day: timeConfig.displayFormat
          }
        },
        ticks: {
          callback: function(value) {
            return formatTick(value, timeConfig.displayFormat);
          }
        },
        title: {
          display: true,
          text: timeZone === 'local' ? 'Time (Local)' : 'Time (UTC)'
        },
        grid: {
          color: '#e0e0e0'
        }
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: yAxisLabel || 'Value'
        },
        grid: {
          color: '#e0e0e0'
        }
      }
    }
  };

  return (
    <div className="chart-container-wrapper">
      {showTitle && <h4 className="chart-title">{title}</h4>}
      <div className="chart-canvas-container" style={chartHeight ? { height: chartHeight } : undefined}>
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

export default TimeSeriesChart;
